import { useState, useEffect, useMemo, lazy, Suspense } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { toast } from 'react-toastify';
import { Mail, Send, Filter, Calendar, TrendingUp, Plus, Edit, Trash2, Eye, EyeOff, Save, X, Copy, List, CheckCircle, Users, Download, FileText, ArrowLeft, Play, Pause, Settings } from "lucide-react";

// Lazy load ReactQuill
const ReactQuill = lazy(() => import('react-quill-new'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CRMTab = () => {
  const [automationView, setAutomationView] = useState("campaigns");
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  
  // Email history/logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogsLoading, setEmailLogsLoading] = useState(false);
  const [emailLogStats, setEmailLogStats] = useState(null);
  const [emailLogFilter, setEmailLogFilter] = useState({ email: '', campaign: '', status: '' });
  const [emailLogPagination, setEmailLogPagination] = useState({ skip: 0, limit: 25, total: 0 });
  
  // Unsubscribe tracking state
  const [unsubscribeLogs, setUnsubscribeLogs] = useState([]);
  const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
  const [unsubscribeCount, setUnsubscribeCount] = useState(null);
  const [unsubscribePagination, setUnsubscribePagination] = useState({ skip: 0, limit: 25, total: 0 });
  
  // Campaign form
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    description: '',
    campaign_type: 'trial_welcome',
    trigger_type: 'signup_trial',
    coupon_code: '',  // Optional: filter by specific coupon code
    is_active: true
  });
  
  // Template form
  const [templateForm, setTemplateForm] = useState({
    name: '',
    subject: '',
    preheader: '',
    html_content: '',
    from_name: 'Luminelle',
    from_email: 'hello@luminellewellness.com'
  });
  
  // Template editor mode (visual or html)
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' or 'html'
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [testEmailTemplateId, setTestEmailTemplateId] = useState(null);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [enrollEmail, setEnrollEmail] = useState('');
  const [enrollCampaignId, setEnrollCampaignId] = useState(null);
  
  // Template starters
  const getTemplateStarter = (type) => {
    const templates = {
      simple: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Welcome, {first_name}!</h1>
  <p>Thank you for being part of our wellness community.</p>
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Best regards,<br/>
    The Luminelle Team
  </p>
</div>`,
      professional: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Hello, {first_name}!</h1>
  <p>We're excited to share this with you.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>💡 Key Information:</strong>
    <p style="margin: 10px 0 0 0;">Add your important message here.</p>
  </div>
  
  <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; margin: 20px 0;">
    Visit Dashboard
  </a>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Questions? Just reply to this email—we're here to help!<br/>
    The Luminelle Team
  </p>
</div>`,
      promotional: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Special Offer for You, {first_name}!</h1>
  <p>Don't miss out on this exclusive opportunity.</p>
  
  <div style="background: linear-gradient(135deg, #8B2635 0%, #1A5E5E 100%); color: white; padding: 30px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <h2 style="margin: 0 0 10px 0; color: white;">Limited Time Offer</h2>
    <p style="font-size: 28px; font-weight: bold; margin: 10px 0; color: #D4A574;">Special Deal</p>
    <p style="margin: 0; color: white;">Available for a limited time only</p>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #D4A574; color: #4A1E1E; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Claim Your Offer
    </a>
  </div>
  
  <p style="text-align: center; font-size: 12px; color: #666;">Offer expires soon. Terms and conditions apply.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    The Luminelle Team<br/>
    <a href="${window.location.origin}" style="color: #8B2635;">Visit Our Website</a>
  </p>
</div>`
    };
    return templates[type] || '';
  };

  // ReactQuill configuration for email templates
  const quillModules = useMemo(() => {
    const modules = {
      toolbar: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['clean']
      ]
    };
    
    return modules;
  }, []);

  const quillFormats = [
    'header', 'bold', 'italic', 'underline',
    'color', 'background', 'list', 'bullet', 'align',
    'link', 'image', 'width', 'height', 'style'
  ];

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchAnalytics();
    // Load ReactQuill CSS for email templates
    import('react-quill-new/dist/quill.snow.css');
    
    // Image resize feature disabled - use HTML to set image sizes if needed
    // eslint-disable-next-line
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/automation/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      toast.error('Failed to fetch campaigns');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/automation/templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
      console.error(error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/admin/automation/analytics`);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    }
  };

  // Fetch email logs/history
  const fetchEmailLogs = async (skip = 0) => {
    setEmailLogsLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', emailLogPagination.limit);
      params.append('skip', skip);
      if (emailLogFilter.email) params.append('user_email', emailLogFilter.email);
      if (emailLogFilter.campaign) params.append('campaign_id', emailLogFilter.campaign);
      if (emailLogFilter.status) params.append('status', emailLogFilter.status);
      
      const response = await axios.get(`${API}/admin/automation/email-logs?${params.toString()}`);
      setEmailLogs(response.data.logs);
      setEmailLogPagination(prev => ({ ...prev, skip, total: response.data.total }));
    } catch (error) {
      console.error('Failed to fetch email logs:', error);
      toast.error('Failed to fetch email history');
    } finally {
      setEmailLogsLoading(false);
    }
  };

  // Fetch email log stats
  const fetchEmailLogStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/automation/email-logs/stats`);
      setEmailLogStats(response.data);
    } catch (error) {
      console.error('Failed to fetch email log stats:', error);
    }
  };

  // Fetch unsubscribe logs
  const fetchUnsubscribeLogs = async (skip = 0) => {
    setUnsubscribeLoading(true);
    try {
      const response = await axios.get(`${API}/admin/automation/unsubscribes?limit=${unsubscribePagination.limit}&skip=${skip}`);
      setUnsubscribeLogs(response.data.logs);
      setUnsubscribePagination(prev => ({ ...prev, skip, total: response.data.total }));
    } catch (error) {
      console.error('Failed to fetch unsubscribe logs:', error);
      toast.error('Failed to fetch unsubscribe data');
    } finally {
      setUnsubscribeLoading(false);
    }
  };

  // Fetch unsubscribe count
  const fetchUnsubscribeCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/automation/unsubscribes/count`);
      setUnsubscribeCount(response.data);
    } catch (error) {
      console.error('Failed to fetch unsubscribe count:', error);
    }
  };

  const createTemplate = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/automation/templates?created_by=admin`,
        templateForm
      );
      toast.success('Template created successfully');
      fetchTemplates();
      fetchAnalytics(); // Update analytics count
      setTemplateForm({
        name: '',
        subject: '',
        preheader: '',
        html_content: '',
        from_name: 'Luminelle',
        from_email: 'hello@luminellewellness.com'
      });
      setEditorMode('visual');
      setAutomationView('templates');
    } catch (error) {
      toast.error('Failed to create template');
      console.error(error);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Please enter an email address');
      return;
    }

    if (!testEmailTemplateId) {
      toast.error('No template selected');
      return;
    }

    try {
      await axios.post(
        `${API}/admin/automation/templates/${testEmailTemplateId}/send-test?test_email=${encodeURIComponent(testEmailAddress)}`
      );
      
      toast.success(`Test email sent to ${testEmailAddress}`);
      setShowTestEmailModal(false);
      setTestEmailAddress('');
      setTestEmailTemplateId(null);
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    }
  };

  const enrollUserInCampaign = async () => {
    if (!enrollEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    if (!enrollCampaignId) {
      toast.error('No campaign selected');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/automation/campaigns/${enrollCampaignId}/enroll?user_email=${encodeURIComponent(enrollEmail)}`
      );
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message} - ${response.data.emails_scheduled} emails scheduled`);
      } else {
        toast.info(response.data.message);
      }
      
      setShowEnrollModal(false);
      setEnrollEmail('');
      setEnrollCampaignId(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enroll user');
      console.error(error);
    }
  };

  const prefillTemplates = async () => {
    if (!confirm('This will create 27 production-ready email templates. Continue?')) return;
    
    const templates = [
  // === 7-DAY TRIAL WELCOME SEQUENCE (8 emails) ===
  {
    name: "Trial Day 0: Welcome to Luminelle",
    subject: "Welcome to Luminelle — You're in the right place.",
    preheader: "Your 7-day journey to feeling good again starts now",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Welcome to Luminelle, {first_name}!</h1>
  <p>Welcome to Luminelle — the space for women 40+ who are done feeling confused by their own bodies and ready to feel good again.</p>
  <p>Over the next 7 days, I'll help you explore the pillars of the site — <strong>Radiant Body, Balanced Mind, Enduring Vitality</strong> — and show you exactly how to get the most out of your trial.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>Quick wins for today:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>Visit the Classroom and watch The Tour videos</li>
      <li>Introduce yourself in Community (we're friendly, I promise)</li>
      <li>Log your first day of symptoms in Tools</li>
    </ul>
  </div>
  
  <p>You're not doing this alone. I'm right here, and I'm excited for you.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Warmly,<br/>
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 1: Meet Your 3 Pillars",
    subject: "Your Body, Mind & Vitality — the Luminelle blueprint",
    preheader: "Understanding the three pillars that guide your wellness journey",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your 3 Pillars, {first_name}</h1>
  <p>Think of Luminelle as a house with three beautifully lit rooms:</p>
  
  <div style="margin: 20px 0;">
    <div style="background: #FFF8E7; padding: 15px; margin-bottom: 10px; border-radius: 4px;">
      <strong style="color: #8B2635;">🌟 Radiant Body</strong>
      <p style="margin: 5px 0 0 0; font-size: 14px;">Skin/hair, agility, weight</p>
    </div>
    <div style="background: #E8F5F5; padding: 15px; margin-bottom: 10px; border-radius: 4px;">
      <strong style="color: #1A5E5E;">🧠 Balanced Mind</strong>
      <p style="margin: 5px 0 0 0; font-size: 14px;">Stress, focus, mood</p>
    </div>
    <div style="background: #FDE8F0; padding: 15px; border-radius: 4px;">
      <strong style="color: #8B2635;">⚡ Enduring Vitality</strong>
      <p style="margin: 5px 0 0 0; font-size: 14px;">Energy, libido, sleep</p>
    </div>
  </div>
  
  <p>Each pillar has Classroom content, recommended products, and tracking tools that work together — not against each other.</p>
  <p><strong>Today, click into the pillar that speaks loudest to you right now.</strong> Start where you are. We'll build from there.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    You've got this.<br/>
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 2: Your Tools Dashboard",
    subject: "Your data is about to become your superpower",
    preheader: "Start tracking to unlock insights about your body",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your Tracking Superpower, {first_name}</h1>
  <p>Here's the truth: our memories are absolutely terrible at keeping score. It's not an age thing—though, sure, you might feel that too. We all overestimate or underestimate our symptoms and progress.</p>
  <p>So I said, "To hell with it—let's build some trackers."</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>The Tools section includes:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>Symptom tracker</li>
      <li>Weight tracker</li>
      <li>Menstrual cycle tracker</li>
    </ul>
  </div>
  
  <p><strong>Today's mission: Track one thing. That's it.</strong></p>
  <p>The magic comes from consistency, not perfection.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 3: Meet Your Community",
    subject: "Ask a question. Any question. Really.",
    preheader: "You're not alone in this journey",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your Community Awaits, {first_name}</h1>
  <p>Inside Luminelle you'll find women who are:</p>
  
  <ul style="line-height: 1.8;">
    <li>Wide awake at 3 a.m.</li>
    <li>Bloated for no good reason</li>
    <li>Suddenly emotional over supermarket commercials</li>
    <li>Crushing it at work but exhausted at home</li>
    <li>Wondering if it's hormones, stress, age, or all of the above</li>
  </ul>
  
  <div style="background: #E8F5F5; padding: 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0; font-size: 16px;"><strong>Post your intro or ask your first question.</strong></p>
    <p style="margin: 10px 0 0 0; font-size: 14px;">I'm in there daily and I always respond.</p>
  </div>
  
  <p>Let's make this journey… well, human.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 4: Marketplace Favorites",
    subject: "The products women 40+ actually feel working",
    preheader: "Discover curated supplements and wellness products",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">The Marketplace, {first_name}</h1>
  <p>Today I want to show you the Marketplace. You'll find supplements, nootropics, and wellness products — only the things I personally research, test, or use myself.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>If you want a starting point, check out:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li><strong>Creatine</strong> — strength, metabolism, brain</li>
      <li><strong>Fisetin</strong> — longevity + inflammation</li>
      <li><strong>Kava</strong> — alcohol alternative + calm</li>
    </ul>
  </div>
  
  <p>Browse by Body, Mind or Vitality to keep it simple.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 5: Personalize Your Path",
    subject: "Make Luminelle work for you — not the other way around",
    preheader: "Customize your experience for maximum impact",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Personalize Your Path, {first_name}</h1>
  <p>We each have a different wellness fingerprint. Today I'll help you personalize your experience:</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <ul style="margin: 10px 0 0 0;">
      <li>Save articles to your Reading List</li>
      <li>Choose your first product to try</li>
      <li>Fill out your Goals if you haven't already</li>
      <li>Bookmark Marketplace items you want to revisit</li>
    </ul>
  </div>
  
  <p>You're building your version of "feeling like yourself again."</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 6: 48 Hours Left",
    subject: "Your trial ends soon — here's what to explore before it does",
    preheader: "Don't miss out on these key features",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">48 Hours Left, {first_name}</h1>
  <p>Your trial wraps up in 48 hours, and I want you to leave no stone unturned.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>Here's what most women value most:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>The Classroom deep dives</li>
      <li>The daily community answers</li>
      <li>The tools (tracking = insight = relief)</li>
      <li>The Marketplace recommendations</li>
    </ul>
  </div>
  
  <p>If you want a personalized plan, send me your top 3 symptoms and I'll point you to what helps most.</p>
  <p><strong>Two days left — make them count.</strong></p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Trial Day 7: Trial Ends Today",
    subject: "Your trial ends today — do you want to keep your access?",
    preheader: "Continue your wellness journey with full membership",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your Trial Ends Today, {first_name}</h1>
  <p>Your 7-day trial ends today. If you've found clarity, answers, community, or even one useful tool — imagine what we can do in 30 days.</p>
  
  <div style="background: linear-gradient(135deg, #8B2635 0%, #1A5E5E 100%); color: white; padding: 30px; text-align: center; border-radius: 8px; margin: 30px 0;">
    <h2 style="margin: 0 0 10px 0; color: white;">Stay a Member to Keep:</h2>
    <ul style="text-align: left; max-width: 400px; margin: 15px auto; color: white;">
      <li>All Classroom content</li>
      <li>Community access + my daily support</li>
      <li>All your tracking tool history</li>
      <li>The Marketplace</li>
      <li>New articles, courses, and tools every week</li>
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}/upgrade" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Continue Your Membership
    </a>
  </div>
  
  <p style="text-align: center;">I'd love to keep supporting you.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },

  // === MEMBER ACTIVATION (3 emails) ===
  {
    name: "Member Day 0: Welcome Official Member",
    subject: "You're in! Let's build your plan.",
    preheader: "Welcome to full access - your wellness journey begins",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Welcome, {first_name}! 🎉</h1>
  <p>I'm thrilled you're here. You now have continued access to everything in Luminelle — no limits, no locked doors.</p>
  
  <div style="background: linear-gradient(135deg, #8B2635 0%, #1A5E5E 100%); color: white; padding: 30px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <h2 style="margin: 0 0 10px 0; color: white;">Exclusive Content Now Available</h2>
    <p style="margin: 0; color: white;">Let's Level Up and begin delving into all things peptide</p>
  </div>
  
  <p>Your future self is already thanking you.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Member Day 1: Choose Your Path",
    subject: "Your wellness journey starts with one choice",
    preheader: "Take the Pillar Quiz to personalize your experience",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Choose Your Path, {first_name}</h1>
  <p>Whether your biggest goal right now is:</p>
  
  <ul style="line-height: 1.8;">
    <li>Better sleep</li>
    <li>More energy</li>
    <li>Weight loss</li>
    <li>Brain clarity</li>
    <li>Skin + hair improvements</li>
    <li>A sense of balance again</li>
  </ul>
  
  <p><strong>There's a personalized path for it.</strong></p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Take Your Pillar Quiz
    </a>
  </div>
  
  <p style="text-align: center;">I'll guide you based on your results.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Member Day 3: Build Your First Protocol",
    subject: "Let's build your Body/Mind/Vitality plan",
    preheader: "Create your personalized wellness protocol",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Build Your Protocol, {first_name}</h1>
  <p>Inside the Tools section is your <strong>Protocol Builder</strong> — a simple way to design your regimen with supplements, peptides, habits, and tracking.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>If you're not sure where to start, you can:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>Browse sample protocols</li>
      <li>Ask me in Community</li>
      <li>Search by symptom</li>
    </ul>
  </div>
  
  <p>Your plan doesn't need to be perfect — it just needs to begin.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },

  // === RE-ENGAGEMENT (1 email) ===
  {
    name: "Inactive User: We Miss You",
    subject: "Haven't seen you around — everything okay?",
    preheader: "Come back and reconnect with your wellness journey",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">We Miss You, {first_name}</h1>
  <p>It's been a minute since you logged into Luminelle.</p>
  <p>Life gets busy. Hormones get weird. Energy dips happen.</p>
  
  <div style="background: #E8F5F5; padding: 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0; font-size: 16px;"><strong>You're not behind — you're just one click away from recalibrating.</strong></p>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Jump Back In
    </a>
  </div>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },

  // === TRANSACTIONAL (3 emails) ===
  {
    name: "Transactional: Payment Successful",
    subject: "Your Luminelle membership is active",
    preheader: "Payment received - you're all set!",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Payment Successful</h1>
  <p>Hi lovely lady — your payment went through and your membership is good to go.</p>
  <p>Thanks for being here.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Transactional: Payment Failed",
    subject: "Oops — update your card?",
    preheader: "We couldn't process your payment",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Payment Issue</h1>
  <p>Looks like your payment didn't go through. It happens to all of us.</p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 16px; font-weight: bold;">
      Update Your Info
    </a>
  </div>
  
  <p style="text-align: center;">We'll hold your access until it's fixed.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Transactional: Membership Cancelled",
    subject: "Your membership has ended",
    preheader: "We're sorry to see you go",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Membership Cancelled</h1>
  <p>Your membership is officially cancelled.</p>
  <p>If you want to come back, you're always welcome.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  }
,
  // === MEMBER ACTIVATION - Additional Days (4 more) ===
  {
    name: "Member Day 5: Join Community Threads",
    subject: "Women who get it, conversations that help",
    preheader: "Your community is waiting for you",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Join the Community, {first_name}</h1>
  <p>Your community is a safe, real, supportive space packed with wisdom — and zero judgment.</p>
  
  <div style="background: #E8F5F5; padding: 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0; font-size: 16px;"><strong>Today's nudge:</strong></p>
    <p style="margin: 10px 0 0 0;">Post one update, one win, or one "WTF is happening to my body?" moment.</p>
  </div>
  
  <p>We're in this together.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Member Day 7: First Week Check-In",
    subject: "One week in — here's what to do next",
    preheader: "Your first week recap and next steps",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">One Week In, {first_name}</h1>
  <p>How are you feeling after your first week?</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>If you haven't yet, explore:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>Your Reading List</li>
      <li>The Classroom in your top pillar</li>
      <li>The Marketplace Starter Picks</li>
    </ul>
  </div>
  
  <p>And drop into Community. I'm there every day.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Member Day 10: Classroom Deep Dive",
    subject: "Ready for deeper understanding?",
    preheader: "Explore advanced wellness content",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Deeper Understanding, {first_name}</h1>
  <p>When women finally understand what's happening inside their bodies, everything gets easier.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>Today, explore:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>GLPs (the good, bad & next wave)</li>
      <li>Sleep science</li>
      <li>Stress + cortisol</li>
      <li>Longevity & immune support</li>
    </ul>
  </div>
  
  <p>Take what you need. Ignore the rest for now.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Member Day 14: Your 30-Day Roadmap",
    subject: "Your next 30 days inside Luminelle",
    preheader: "Setting goals for your first month",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your 30-Day Roadmap, {first_name}</h1>
  <p>You've explored. You've learned. You've tracked. You've asked questions. Now it's time to structure the next month.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>Here's your roadmap:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>Commit to 1–2 pillar goals</li>
      <li>Track at least 3 times/week</li>
      <li>Check Community on tough days</li>
      <li>Use the Marketplace when you're ready to level up</li>
    </ul>
  </div>
  
  <p>You're already making moves — let's keep going.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },

  // === INACTIVE MEMBER DRIP - Additional (3 more) ===
  {
    name: "Inactive: What's New Since You Left",
    subject: "You've missed some good stuff…",
    preheader: "New content and features added",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">What's New, {first_name}</h1>
  <p>Since you last logged in, we've added:</p>
  
  <ul style="line-height: 1.8;">
    <li>New articles</li>
    <li>New community discussions</li>
    <li>New Marketplace products</li>
  </ul>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      See What's New
    </a>
  </div>
  
  <p style="text-align: center;">Come check out what's new — I think you'll love it.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Inactive: Pick One Tiny Step",
    subject: "Pick the easiest possible win today",
    preheader: "Small steps lead to big momentum",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">One Tiny Step, {first_name}</h1>
  <p>Don't overhaul your life today.</p>
  
  <div style="background: #E8F5F5; padding: 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0; font-size: 16px;"><strong>Just track one thing. Read one thing. Ask one question.</strong></p>
    <p style="margin: 10px 0 0 0;">You'll feel better immediately.</p>
  </div>
  
  <p>Momentum starts small.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Inactive: Your Restart Plan",
    subject: "Let's restart — gently.",
    preheader: "A simple 3-day reset to get back on track",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your Restart Plan, {first_name}</h1>
  <p>Here's a simple 3-day reset:</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <ul style="margin: 10px 0 0 0;">
      <li><strong>Day 1:</strong> Track one symptom</li>
      <li><strong>Day 2:</strong> Read one short article</li>
      <li><strong>Day 3:</strong> Post a question or win in Community</li>
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Start Day 1
    </a>
  </div>
  
  <p style="text-align: center;">You're back.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },

  // === CANCELLATION / WINBACK (5 emails) ===
  {
    name: "Cancel Save: Customize Your Plan",
    subject: "Before you go — can we adjust your plan?",
    preheader: "Let's find what works for you",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Before You Go, {first_name}</h1>
  <p>You clicked cancel, and while I respect your choice, I'd love to understand if something wasn't working or if you need a different approach.</p>
  
  <div style="background: #FFF8E7; padding: 15px; border-left: 4px solid #D4A574; margin: 20px 0;">
    <strong>If you want, I can help you:</strong>
    <ul style="margin: 10px 0 0 0;">
      <li>Recalibrate your protocol</li>
      <li>Simplify your plan</li>
      <li>Focus on the symptoms bothering you most</li>
      <li>Personalize your path</li>
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Adjust My Plan Instead
    </a>
  </div>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Warmly,<br/>
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Cancel Save: 7-Day Extension Offer",
    subject: "Keep your access for 7 more days?",
    preheader: "Free extension to make sure you're confident",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">7 More Days, {first_name}?</h1>
  <p>If you're not ready to fully step away, I can extend your membership for 7 more days at no charge — just to make sure you're confident in your decision.</p>
  
  <div style="background: linear-gradient(135deg, #8B2635 0%, #1A5E5E 100%); color: white; padding: 30px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <h2 style="margin: 0 0 10px 0; color: white;">7 Days Free</h2>
    <p style="margin: 0; color: white;">Use coupon: <strong>EXPLOREMORE7</strong></p>
  </div>
  
  <p style="text-align: center;">Want the extra time?</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Winback: We've Missed You",
    subject: "There's a lot that's changed since you left…",
    preheader: "New content, products, and features await",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">We've Missed You, {first_name}</h1>
  <p>We've added:</p>
  
  <ul style="line-height: 1.8;">
    <li>New Classroom content</li>
    <li>New Marketplace items</li>
    <li>New tools</li>
    <li>New discussions</li>
  </ul>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="${window.location.origin}" style="display: inline-block; background: #8B2635; color: white; padding: 15px 40px; text-decoration: none; border-radius: 4px; font-size: 18px; font-weight: bold;">
      Welcome Back
    </a>
  </div>
  
  <p style="text-align: center;">If you want to come back, your account is waiting for you.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Winback: Personalized Restart",
    subject: "Want me to build you a new plan?",
    preheader: "Custom protocol designed just for you",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Your Custom Plan, {first_name}</h1>
  <p>If you're ready to jump back in, reply with your top 3 symptoms and I'll create a plan for you — no guesswork.</p>
  
  <div style="background: #E8F5F5; padding: 20px; text-align: center; border-radius: 8px; margin: 20px 0;">
    <p style="margin: 0; font-size: 16px;"><strong>Personalized. Simple. Effective.</strong></p>
  </div>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  },
  {
    name: "Transactional: Membership Renewed",
    subject: "Your membership just renewed",
    preheader: "Thank you for staying with us",
    html_content: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; color: #4A1E1E;">
  <img src="https://customer-assets.emergentagent.com/job_menopause-app/artifacts/2r5a6l8z_Luminelle%20logo%20horizontal%20web.png" alt="Luminelle" style="width: 150px; margin-bottom: 20px; display: block;" />
  <h1 style="color: #8B2635;">Membership Renewed</h1>
  <p>Thanks for sticking with us. I'm excited for what we'll explore next together.</p>
  
  <p style="margin-top: 30px; font-size: 14px; color: #666;">
    Lauren
  </p>
</div>`,
    from_name: "Lauren from Luminelle",
    from_email: "hello@luminellewellness.com"
  }

];

    try {
      let created = 0;
      for (const template of templates) {
        try {
          await axios.post(`${API}/admin/automation/templates?created_by=admin`, template);
          created++;
        } catch (error) {
          console.error(`Failed to create template ${template.name}:`, error);
        }
      }
      toast.success(`Created ${created} email templates successfully!`);
      fetchTemplates();
      fetchAnalytics();
    } catch (error) {
      toast.error('Failed to create templates');
      console.error(error);
    }
  };

  const createCampaign = async () => {
    try {
      const response = await axios.post(
        `${API}/admin/automation/campaigns?created_by=admin`,
        campaignForm
      );
      toast.success('Campaign created successfully');
      fetchCampaigns();
      setSelectedCampaign(response.data);
      setAutomationView('edit-campaign');
    } catch (error) {
      toast.error('Failed to create campaign');
      console.error(error);
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!confirm('Are you sure you want to delete this campaign?')) return;
    
    try {
      await axios.delete(`${API}/admin/automation/campaigns/${campaignId}`);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to delete campaign');
      console.error(error);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/admin/automation/templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
      fetchAnalytics(); // Update analytics count
    } catch (error) {
      toast.error('Failed to delete template');
      console.error(error);
    }
  };

  const duplicateTemplate = async (template) => {
    try {
      const duplicatedTemplate = {
        name: `${template.name} (Copy)`,
        subject: template.subject,
        preheader: template.preheader || '',
        html_content: template.html_content,
        from_name: template.from_name,
        from_email: template.from_email
      };
      
      await axios.post(
        `${API}/admin/automation/templates?created_by=admin`,
        duplicatedTemplate
      );
      toast.success('Template duplicated successfully');
      fetchTemplates();
      fetchAnalytics(); // Update analytics count
    } catch (error) {
      toast.error('Failed to duplicate template');
      console.error(error);
    }
  };

  const toggleCampaignActive = async (campaignId, isActive) => {
    try {
      await axios.put(`${API}/admin/automation/campaigns/${campaignId}`, {
        is_active: !isActive
      });
      toast.success(isActive ? 'Campaign deactivated' : 'Campaign activated');
      fetchCampaigns();
    } catch (error) {
      toast.error('Failed to update campaign');
      console.error(error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Analytics */}
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-bold text-burgundy font-serif mb-2">Email Automation</h2>
          <p className="text-burgundy/60 text-sm">Manage drip campaigns, templates, and automation triggers</p>
        </div>
        {analytics && (
          <div className="grid grid-cols-3 gap-4">
            <Card className="border-burgundy/20">
              <CardContent className="p-3">
                <div className="text-xs text-burgundy/60">Active Campaigns</div>
                <div className="text-2xl font-bold text-burgundy">{analytics.active_campaigns}</div>
              </CardContent>
            </Card>
            <Card className="border-burgundy/20">
              <CardContent className="p-3">
                <div className="text-xs text-burgundy/60">Templates</div>
                <div className="text-2xl font-bold text-burgundy">{analytics.total_templates}</div>
              </CardContent>
            </Card>
            <Card className="border-burgundy/20">
              <CardContent className="p-3">
                <div className="text-xs text-burgundy/60">Emails Sent</div>
                <div className="text-2xl font-bold text-burgundy">{analytics.sent_emails}</div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-burgundy/20 mb-6">
        <button
          onClick={() => setAutomationView('campaigns')}
          className={`px-4 py-2 font-medium transition-colors ${
            automationView === 'campaigns'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-burgundy/50 hover:text-burgundy'
          }`}
        >
          Campaigns
        </button>
        <button
          onClick={() => setAutomationView('templates')}
          className={`px-4 py-2 font-medium transition-colors ${
            automationView === 'templates'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-burgundy/50 hover:text-burgundy'
          }`}
        >
          Templates
        </button>
        <button
          onClick={() => {
            setAutomationView('email-history');
            fetchEmailLogs();
            fetchEmailLogStats();
          }}
          className={`px-4 py-2 font-medium transition-colors ${
            automationView === 'email-history'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-burgundy/50 hover:text-burgundy'
          }`}
        >
          Email History
        </button>
        <button
          onClick={() => {
            setAutomationView('unsubscribes');
            fetchUnsubscribeLogs();
            fetchUnsubscribeCount();
          }}
          className={`px-4 py-2 font-medium transition-colors ${
            automationView === 'unsubscribes'
              ? 'text-burgundy border-b-2 border-burgundy'
              : 'text-burgundy/50 hover:text-burgundy'
          }`}
        >
          Unsubscribes
        </button>
      </div>

      {/* Unsubscribes View */}
      {automationView === 'unsubscribes' && (
        <div className="space-y-4">
          {/* Stats */}
          {unsubscribeCount && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Card className="border-burgundy/20">
                <CardContent className="p-4">
                  <div className="text-xs text-burgundy/60">Total Unsubscribed</div>
                  <div className="text-3xl font-bold text-red-600">{unsubscribeCount.total_unsubscribed}</div>
                </CardContent>
              </Card>
              <Card className="border-burgundy/20">
                <CardContent className="p-4">
                  <div className="text-xs text-burgundy/60">Users with Emails Disabled</div>
                  <div className="text-3xl font-bold text-burgundy">{unsubscribeCount.users_with_emails_disabled}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Unsubscribe Logs Table */}
          <Card className="border-burgundy/20">
            <CardHeader>
              <CardTitle className="text-lg text-burgundy">
                Unsubscribe History
                <span className="text-sm font-normal text-burgundy/60 ml-2">
                  ({unsubscribePagination.total} total)
                </span>
              </CardTitle>
              <CardDescription className="text-burgundy/60">
                Users who have opted out of marketing emails
              </CardDescription>
            </CardHeader>
            <CardContent>
              {unsubscribeLoading ? (
                <div className="text-center py-8 text-burgundy/50">Loading...</div>
              ) : unsubscribeLogs.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
                  <p className="text-burgundy/70 font-medium">No unsubscribes yet!</p>
                  <p className="text-burgundy/50 text-sm">All your subscribers are still engaged.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-burgundy/20">
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Email</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Name</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Reason</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Unsubscribed At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unsubscribeLogs.map((log) => (
                          <tr key={log.id} className="border-b border-burgundy/10 hover:bg-burgundy/5">
                            <td className="py-2 px-3 text-burgundy">{log.user_email}</td>
                            <td className="py-2 px-3 text-burgundy/80">{log.user_name || '-'}</td>
                            <td className="py-2 px-3">
                              <Badge className="bg-red-100 text-red-700">
                                {log.reason?.replace(/_/g, ' ') || 'Opted out'}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-burgundy/60 text-xs">
                              {new Date(log.unsubscribed_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {unsubscribePagination.total > unsubscribePagination.limit && (
                    <div className="flex justify-between items-center mt-4">
                      <div className="text-sm text-burgundy/60">
                        Showing {unsubscribePagination.skip + 1}-{Math.min(unsubscribePagination.skip + unsubscribePagination.limit, unsubscribePagination.total)} of {unsubscribePagination.total}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={unsubscribePagination.skip === 0}
                          onClick={() => fetchUnsubscribeLogs(Math.max(0, unsubscribePagination.skip - unsubscribePagination.limit))}
                          className="border-burgundy/30 text-burgundy"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={unsubscribePagination.skip + unsubscribePagination.limit >= unsubscribePagination.total}
                          onClick={() => fetchUnsubscribeLogs(unsubscribePagination.skip + unsubscribePagination.limit)}
                          className="border-burgundy/30 text-burgundy"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Email History View */}
      {automationView === 'email-history' && (
        <div className="space-y-4">
          {/* Stats Cards */}
          {emailLogStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="border-burgundy/20">
                <CardContent className="p-3">
                  <div className="text-xs text-burgundy/60">Total Sent</div>
                  <div className="text-2xl font-bold text-burgundy">{emailLogStats.total_sent}</div>
                </CardContent>
              </Card>
              <Card className="border-burgundy/20">
                <CardContent className="p-3">
                  <div className="text-xs text-burgundy/60">Last 24 Hours</div>
                  <div className="text-2xl font-bold text-green-600">{emailLogStats.sent_last_24h}</div>
                </CardContent>
              </Card>
              <Card className="border-burgundy/20">
                <CardContent className="p-3">
                  <div className="text-xs text-burgundy/60">Last 7 Days</div>
                  <div className="text-2xl font-bold text-burgundy">{emailLogStats.sent_last_7_days}</div>
                </CardContent>
              </Card>
              <Card className="border-burgundy/20">
                <CardContent className="p-3">
                  <div className="text-xs text-burgundy/60">Failed</div>
                  <div className="text-2xl font-bold text-red-600">{emailLogStats.total_failed}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <Card className="border-burgundy/20">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Search by Email</label>
                  <Input
                    placeholder="user@example.com"
                    value={emailLogFilter.email}
                    onChange={(e) => setEmailLogFilter(prev => ({ ...prev, email: e.target.value }))}
                    className="border-burgundy/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Campaign</label>
                  <select
                    value={emailLogFilter.campaign}
                    onChange={(e) => setEmailLogFilter(prev => ({ ...prev, campaign: e.target.value }))}
                    className="w-full p-2 border border-burgundy/20 rounded-md text-sm"
                  >
                    <option value="">All Campaigns</option>
                    {campaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Status</label>
                  <select
                    value={emailLogFilter.status}
                    onChange={(e) => setEmailLogFilter(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full p-2 border border-burgundy/20 rounded-md text-sm"
                  >
                    <option value="">All Status</option>
                    <option value="sent">Sent</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={() => fetchEmailLogs(0)}
                    className="bg-burgundy hover:bg-burgundy/80 w-full"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filter
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Email Logs Table */}
          <Card className="border-burgundy/20">
            <CardHeader>
              <CardTitle className="text-lg text-burgundy">
                Email Delivery History
                <span className="text-sm font-normal text-burgundy/60 ml-2">
                  ({emailLogPagination.total} total)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {emailLogsLoading ? (
                <div className="text-center py-8 text-burgundy/50">Loading...</div>
              ) : emailLogs.length === 0 ? (
                <div className="text-center py-8">
                  <Mail className="h-12 w-12 text-burgundy/30 mx-auto mb-4" />
                  <p className="text-burgundy/50">No emails found</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-burgundy/20">
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Recipient</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Subject</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Campaign</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Status</th>
                          <th className="text-left py-2 px-3 text-burgundy font-semibold">Sent At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {emailLogs.map((log) => (
                          <tr key={log.id} className="border-b border-burgundy/10 hover:bg-burgundy/5">
                            <td className="py-2 px-3 text-burgundy">{log.user_email}</td>
                            <td className="py-2 px-3 text-burgundy/80 max-w-xs truncate">{log.subject}</td>
                            <td className="py-2 px-3 text-burgundy/60">{log.campaign_name || '-'}</td>
                            <td className="py-2 px-3">
                              <Badge className={log.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                                {log.status}
                              </Badge>
                            </td>
                            <td className="py-2 px-3 text-burgundy/60 text-xs">
                              {new Date(log.sent_at).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {emailLogPagination.total > emailLogPagination.limit && (
                    <div className="flex justify-between items-center mt-4">
                      <div className="text-sm text-burgundy/60">
                        Showing {emailLogPagination.skip + 1}-{Math.min(emailLogPagination.skip + emailLogPagination.limit, emailLogPagination.total)} of {emailLogPagination.total}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={emailLogPagination.skip === 0}
                          onClick={() => fetchEmailLogs(Math.max(0, emailLogPagination.skip - emailLogPagination.limit))}
                          className="border-burgundy/30 text-burgundy"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={emailLogPagination.skip + emailLogPagination.limit >= emailLogPagination.total}
                          onClick={() => fetchEmailLogs(emailLogPagination.skip + emailLogPagination.limit)}
                          className="border-burgundy/30 text-burgundy"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Campaigns View */}
      {automationView === 'campaigns' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-burgundy">Automation Campaigns</h3>
            <Button
              onClick={() => {
                setCampaignForm({
                  name: '',
                  description: '',
                  campaign_type: 'trial_welcome',
                  trigger_type: 'signup_trial',
                  coupon_code: '',
                  is_active: false
                });
                setAutomationView('create-campaign');
              }}
              className="bg-burgundy hover:bg-burgundy/80"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-burgundy/50">Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <Card className="border-burgundy/20">
              <CardContent className="p-8 text-center">
                <Mail className="h-12 w-12 text-burgundy/30 mx-auto mb-4" />
                <p className="text-burgundy/60 mb-4">No campaigns yet</p>
                <Button
                  onClick={() => setAutomationView('create-campaign')}
                  className="bg-burgundy hover:bg-burgundy/80"
                >
                  Create Your First Campaign
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {campaigns.map((campaign) => (
                <Card key={campaign.id} className="border-burgundy/20 hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-burgundy">{campaign.name}</h4>
                          <Badge className={campaign.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                            {campaign.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                          <Badge variant="outline" className="border-burgundy/30 text-burgundy/70">
                            {campaign.campaign_type.replace(/_/g, ' ')}
                          </Badge>
                        </div>
                        <p className="text-sm text-burgundy/60 mb-3">{campaign.description}</p>
                        <div className="flex flex-wrap items-center gap-4 text-xs text-burgundy/50">
                          <span>📧 {campaign.emails?.length || 0} emails</span>
                          <span>🎯 {campaign.total_triggered || 0} triggered</span>
                          <span>✅ {campaign.total_completed || 0} completed</span>
                          <span>Trigger: {campaign.trigger_type.replace(/_/g, ' ')}</span>
                          {campaign.coupon_code && (
                            <Badge className="bg-gold/20 text-burgundy border-gold/30">
                              Coupon: {campaign.coupon_code}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedCampaign(campaign);
                            setAutomationView('edit-campaign');
                          }}
                          className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleCampaignActive(campaign.id, campaign.is_active)}
                          className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
                        >
                          {campaign.is_active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEnrollCampaignId(campaign.id);
                            setShowEnrollModal(true);
                          }}
                          className="border-green-300 text-green-600 hover:bg-green-50"
                        >
                          <Users className="h-4 w-4 mr-1" />
                          Enroll Test User
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => deleteCampaign(campaign.id)}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Templates View */}
      {automationView === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-burgundy">Email Templates</h3>
            <div className="flex gap-2">
              <Button
                onClick={prefillTemplates}
                variant="outline"
                className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
              >
                <Download className="h-4 w-4 mr-2" />
                Load Pre-filled Templates (27)
              </Button>
              <Button
                onClick={() => {
                  setTemplateForm({
                    name: '',
                    subject: '',
                    preheader: '',
                    html_content: '',
                    from_name: 'Luminelle',
                    from_email: 'hello@luminellewellness.com'
                  });
                  setAutomationView('create-template');
                }}
                className="bg-burgundy hover:bg-burgundy/80"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Template
              </Button>
            </div>
          </div>

          {templates.length === 0 ? (
            <Card className="border-burgundy/20">
              <CardContent className="p-8 text-center">
                <FileText className="h-12 w-12 text-burgundy/30 mx-auto mb-4" />
                <p className="text-burgundy/60 mb-4">No templates yet</p>
                <Button
                  onClick={() => setAutomationView('create-template')}
                  className="bg-burgundy hover:bg-burgundy/80"
                >
                  Create Your First Template
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {templates.map((template) => (
                <Card key={template.id} className="border-burgundy/20 hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold text-burgundy">{template.name}</h4>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setAutomationView('preview-template');
                          }}
                          className="text-blue-600 hover:bg-blue-50"
                          title="Preview email"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setTestEmailTemplateId(template.id);
                            setShowTestEmailModal(true);
                          }}
                          className="text-purple-600 hover:bg-purple-50"
                          title="Send test email"
                        >
                          <Send className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setSelectedTemplate(template);
                            setTemplateForm({
                              name: template.name,
                              subject: template.subject,
                              preheader: template.preheader || '',
                              html_content: template.html_content,
                              from_name: template.from_name,
                              from_email: template.from_email
                            });
                            setAutomationView('edit-template');
                          }}
                          className="text-burgundy hover:bg-burgundy/10"
                          title="Edit template"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => duplicateTemplate(template)}
                          className="text-green-600 hover:bg-green-50"
                          title="Duplicate template"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteTemplate(template.id)}
                          className="text-red-600 hover:bg-red-50"
                          title="Delete template"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-burgundy/60 mb-1">
                      <strong>Subject:</strong> {template.subject}
                    </div>
                    {template.preheader && (
                      <div className="text-xs text-burgundy/50 mb-2">
                        Preview: {template.preheader.substring(0, 60)}...
                      </div>
                    )}
                    <div className="text-xs text-burgundy/40">
                      From: {template.from_name} {'<'}{template.from_email}{'>'}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Campaign Form */}
      {automationView === 'create-campaign' && (
        <Card className="border-burgundy/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-burgundy">Create New Campaign</h3>
                <p className="text-sm text-burgundy/60">Set up an automated email sequence</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => setAutomationView('campaigns')}
                className="text-burgundy/60"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Campaign Name</label>
              <Input
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({...campaignForm, name: e.target.value})}
                placeholder="e.g., 7-Day Trial Welcome Drip"
                className="border-burgundy/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Description</label>
              <textarea
                value={campaignForm.description}
                onChange={(e) => setCampaignForm({...campaignForm, description: e.target.value})}
                placeholder="Describe what this campaign does"
                className="w-full p-2 border border-burgundy/20 rounded-md"
                rows="3"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Campaign Type</label>
                <select
                  value={campaignForm.campaign_type}
                  onChange={(e) => setCampaignForm({...campaignForm, campaign_type: e.target.value})}
                  className="w-full p-2 border border-burgundy/20 rounded-md"
                >
                  <option value="trial_welcome">Trial Welcome (A1)</option>
                  <option value="trial_expiration">Trial Expiration (A2)</option>
                  <option value="member_activation">Member Activation (B1)</option>
                  <option value="monthly_nudge">Monthly Nudge (B2)</option>
                  <option value="transactional">Transactional (E1)</option>
                  <option value="save_winback">Save & Winback (E2)</option>
                  <option value="manual_broadcast">Manual Broadcast</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Trigger</label>
                <select
                  value={campaignForm.trigger_type}
                  onChange={(e) => setCampaignForm({...campaignForm, trigger_type: e.target.value})}
                  className="w-full p-2 border border-burgundy/20 rounded-md"
                >
                  <option value="signup_trial">Trial Signup (7 days)</option>
                  <option value="signup_trial_extended">Trial Signup - Extended (14 days)</option>
                  <option value="signup_with_coupon">Signup with Coupon (specify below)</option>
                  <option value="signup_forever_free">Signup - Forever Free Coupon</option>
                  <option value="trial_expiring">Trial Expiring</option>
                  <option value="trial_expired">Trial Expired</option>
                  <option value="payment_success">Payment Success (Stripe)</option>
                  <option value="payment_failed">Payment Failed</option>
                  <option value="subscription_renewed">Subscription Renewed</option>
                  <option value="subscription_paused">Subscription Paused</option>
                  <option value="subscription_cancelled">Subscription Cancelled</option>
                  <option value="refund_processed">Refund Processed</option>
                  <option value="inactivity">User Inactivity</option>
                  <option value="manual">Manual Trigger</option>
                </select>
              </div>
            </div>
            
            {/* Coupon Code Filter - shown when signup_with_coupon is selected */}
            {campaignForm.trigger_type === 'signup_with_coupon' && (
              <div className="bg-gold/10 border border-gold/30 rounded-lg p-4">
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Coupon Code (required for this trigger)
                </label>
                <Input
                  value={campaignForm.coupon_code}
                  onChange={(e) => setCampaignForm({...campaignForm, coupon_code: e.target.value.toUpperCase()})}
                  placeholder="e.g., WELCOME2024, PARTNER50"
                  className="border-burgundy/20 uppercase"
                />
                <p className="text-xs text-burgundy/60 mt-1">
                  This campaign will only trigger for users who sign up with this specific coupon code
                </p>
              </div>
            )}
            
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={campaignForm.is_active}
                onChange={(e) => setCampaignForm({...campaignForm, is_active: e.target.checked})}
                className="rounded border-burgundy/20"
              />
              <label className="text-sm text-burgundy">Activate campaign immediately</label>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={createCampaign}
                className="bg-burgundy hover:bg-burgundy/80"
              >
                Create Campaign
              </Button>
              <Button
                variant="outline"
                onClick={() => setAutomationView('campaigns')}
                className="border-burgundy/30"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Template Form */}
      {automationView === 'create-template' && (
        <Card className="border-burgundy/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-burgundy">Create New Template</h3>
                <p className="text-sm text-burgundy/60">Design an email template</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setAutomationView('templates');
                  setEditorMode('visual');
                }}
                className="text-burgundy/60"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Template Starter Selection */}
            <div className="bg-gold/10 border border-gold/30 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-burgundy mb-3">Choose a Template Starter</h4>
              <div className="grid grid-cols-3 gap-3">
                <button
                  type="button"
                  onClick={() => setTemplateForm({...templateForm, html_content: getTemplateStarter('simple')})}
                  className="p-4 border-2 border-burgundy/20 rounded-lg hover:border-burgundy/50 hover:bg-burgundy/5 transition-all text-left"
                >
                  <div className="font-medium text-burgundy text-sm mb-1">Simple</div>
                  <div className="text-xs text-burgundy/60">Clean and minimal design</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateForm({...templateForm, html_content: getTemplateStarter('professional')})}
                  className="p-4 border-2 border-burgundy/20 rounded-lg hover:border-burgundy/50 hover:bg-burgundy/5 transition-all text-left"
                >
                  <div className="font-medium text-burgundy text-sm mb-1">Professional</div>
                  <div className="text-xs text-burgundy/60">Branded with highlights</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateForm({...templateForm, html_content: getTemplateStarter('promotional')})}
                  className="p-4 border-2 border-burgundy/20 rounded-lg hover:border-burgundy/50 hover:bg-burgundy/5 transition-all text-left"
                >
                  <div className="font-medium text-burgundy text-sm mb-1">Promotional</div>
                  <div className="text-xs text-burgundy/60">Eye-catching with CTA</div>
                </button>
              </div>
              <p className="text-xs text-burgundy/50 mt-2">Click a template to start with a pre-designed layout</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Template Name</label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                placeholder="e.g., Day 0: Welcome to Luminelle"
                className="border-burgundy/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Subject Line</label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                placeholder="Email subject"
                className="border-burgundy/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Preheader Text (Optional)</label>
              <Input
                value={templateForm.preheader}
                onChange={(e) => setTemplateForm({...templateForm, preheader: e.target.value})}
                placeholder="Preview text that appears in inbox"
                className="border-burgundy/20"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">From Name</label>
                <Input
                  value={templateForm.from_name}
                  onChange={(e) => setTemplateForm({...templateForm, from_name: e.target.value})}
                  className="border-burgundy/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">From Email</label>
                <Input
                  value={templateForm.from_email}
                  onChange={(e) => setTemplateForm({...templateForm, from_email: e.target.value})}
                  className="border-burgundy/20"
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-burgundy">Email Content</label>
                <div className="flex gap-1 bg-burgundy/10 rounded-md p-1">
                  <button
                    type="button"
                    onClick={() => setEditorMode('visual')}
                    className={`px-3 py-1 text-xs rounded ${editorMode === 'visual' ? 'bg-burgundy text-cream' : 'text-burgundy hover:bg-burgundy/20'}`}
                  >
                    Visual Editor
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorMode('html')}
                    className={`px-3 py-1 text-xs rounded ${editorMode === 'html' ? 'bg-burgundy text-cream' : 'text-burgundy hover:bg-burgundy/20'}`}
                  >
                    HTML Code
                  </button>
                </div>
              </div>
              
              {editorMode === 'visual' ? (
                <div className="border border-burgundy/30 rounded-lg overflow-hidden">
                  <Suspense fallback={
                    <div className="border border-burgundy/30 rounded-lg p-4 min-h-[300px] bg-gray-50 flex items-center justify-center">
                      <div className="text-burgundy/60">Loading editor...</div>
                    </div>
                  }>
                    <ReactQuill
                      theme="snow"
                      value={templateForm.html_content}
                      onChange={(value) => setTemplateForm({...templateForm, html_content: value})}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ minHeight: '300px' }}
                      placeholder="Write your email content with formatting..."
                    />
                  </Suspense>
                </div>
              ) : (
                <textarea
                  value={templateForm.html_content}
                  onChange={(e) => setTemplateForm({...templateForm, html_content: e.target.value})}
                  className="w-full p-3 border border-burgundy/20 rounded-md font-mono text-sm"
                  rows="12"
                  placeholder="Enter HTML code..."
                />
              )}
              <p className="text-xs text-burgundy/50 mt-1">
                Tip: Use {'{'}first_name{'}'}, {'{'}last_name{'}'}, {'{'}email{'}'} as placeholders for personalization
              </p>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={createTemplate}
                className="bg-burgundy hover:bg-burgundy/80"
              >
                Create Template
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setAutomationView('templates');
                  setEditorMode('visual');
                }}
                className="border-burgundy/30"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Template Form */}
      {automationView === 'edit-template' && selectedTemplate && (
        <Card className="border-burgundy/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-burgundy">Edit Template</h3>
                <p className="text-sm text-burgundy/60">Update your email template</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setAutomationView('templates');
                  setSelectedTemplate(null);
                }}
                className="text-burgundy/60"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Template Name</label>
              <Input
                value={templateForm.name}
                onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
                className="border-burgundy/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Subject Line</label>
              <Input
                value={templateForm.subject}
                onChange={(e) => setTemplateForm({...templateForm, subject: e.target.value})}
                className="border-burgundy/20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-burgundy mb-1">Preheader Text (Optional)</label>
              <Input
                value={templateForm.preheader}
                onChange={(e) => setTemplateForm({...templateForm, preheader: e.target.value})}
                className="border-burgundy/20"
              />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">From Name</label>
                <Input
                  value={templateForm.from_name}
                  onChange={(e) => setTemplateForm({...templateForm, from_name: e.target.value})}
                  className="border-burgundy/20"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">From Email</label>
                <Input
                  value={templateForm.from_email}
                  onChange={(e) => setTemplateForm({...templateForm, from_email: e.target.value})}
                  className="border-burgundy/20"
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-burgundy">Email Content</label>
                <div className="flex gap-1 bg-burgundy/10 rounded-md p-1">
                  <button
                    type="button"
                    onClick={() => setEditorMode('visual')}
                    className={`px-3 py-1 text-xs rounded ${editorMode === 'visual' ? 'bg-burgundy text-cream' : 'text-burgundy hover:bg-burgundy/20'}`}
                  >
                    Visual Editor
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditorMode('html')}
                    className={`px-3 py-1 text-xs rounded ${editorMode === 'html' ? 'bg-burgundy text-cream' : 'text-burgundy hover:bg-burgundy/20'}`}
                  >
                    HTML Code
                  </button>
                </div>
              </div>
              
              {editorMode === 'visual' ? (
                <div className="border border-burgundy/30 rounded-lg overflow-hidden">
                  <Suspense fallback={
                    <div className="border border-burgundy/30 rounded-lg p-4 min-h-[300px] bg-gray-50 flex items-center justify-center">
                      <div className="text-burgundy/60">Loading editor...</div>
                    </div>
                  }>
                    <ReactQuill
                      theme="snow"
                      value={templateForm.html_content}
                      onChange={(value) => setTemplateForm({...templateForm, html_content: value})}
                      modules={quillModules}
                      formats={quillFormats}
                      style={{ minHeight: '300px' }}
                      placeholder="Write your email content with formatting..."
                    />
                  </Suspense>
                </div>
              ) : (
                <textarea
                  value={templateForm.html_content}
                  onChange={(e) => setTemplateForm({...templateForm, html_content: e.target.value})}
                  className="w-full p-3 border border-burgundy/20 rounded-md font-mono text-sm"
                  rows="12"
                  placeholder="Enter HTML code..."
                />
              )}
              <p className="text-xs text-burgundy/50 mt-1">
                Tip: Use {'{'}first_name{'}'}, {'{'}last_name{'}'}, {'{'}email{'}'} as placeholders for personalization
              </p>
            </div>
            <div className="flex gap-2 pt-4">
              <Button
                onClick={async () => {
                  try {
                    await axios.put(`${API}/admin/automation/templates/${selectedTemplate.id}`, templateForm);
                    toast.success('Template updated successfully');
                    fetchTemplates();
                    fetchAnalytics(); // Update analytics count
                    setAutomationView('templates');
                    setSelectedTemplate(null);
                    setEditorMode('visual');
                  } catch (error) {
                    toast.error('Failed to update template');
                    console.error(error);
                  }
                }}
                className="bg-burgundy hover:bg-burgundy/80"
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setAutomationView('templates');
                  setSelectedTemplate(null);
                  setEditorMode('visual');
                }}
                className="border-burgundy/30"
              >
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Template */}
      {automationView === 'preview-template' && selectedTemplate && (
        <Card className="border-burgundy/20">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-burgundy">Email Preview</h3>
                <p className="text-sm text-burgundy/60">{selectedTemplate.name}</p>
              </div>
              <Button
                variant="ghost"
                onClick={() => {
                  setAutomationView('templates');
                  setSelectedTemplate(null);
                }}
                className="text-burgundy/60"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email Header Info */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <div className="text-sm space-y-2">
                <div><strong>From:</strong> {selectedTemplate.from_name} &lt;{selectedTemplate.from_email}&gt;</div>
                <div><strong>Subject:</strong> {selectedTemplate.subject}</div>
                {selectedTemplate.preheader && (
                  <div><strong>Preview Text:</strong> {selectedTemplate.preheader}</div>
                )}
              </div>
            </div>

            {/* Email Body Preview */}
            <div className="border border-burgundy/20 rounded-lg p-6 bg-white">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ 
                  __html: selectedTemplate.html_content
                    .replace(/{first_name}/g, 'Sarah')
                    .replace(/{last_name}/g, 'Smith')
                    .replace(/{email}/g, 'sarah@example.com')
                }}
              />
            </div>

            {/* Helper Text */}
            <div className="text-xs text-burgundy/50 bg-blue-50 p-3 rounded">
              <strong>Note:</strong> Placeholders like {'{first_name}'} are shown with example data. 
              They'll be replaced with real user data when sent.
            </div>

            <div className="flex gap-2">
              <Button
                onClick={() => {
                  setSelectedTemplate(selectedTemplate);
                  setTemplateForm({
                    name: selectedTemplate.name,
                    subject: selectedTemplate.subject,
                    preheader: selectedTemplate.preheader || '',
                    html_content: selectedTemplate.html_content,
                    from_name: selectedTemplate.from_name,
                    from_email: selectedTemplate.from_email
                  });
                  setAutomationView('edit-template');
                }}
                className="bg-burgundy hover:bg-burgundy/80"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit This Template
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setAutomationView('templates');
                  setSelectedTemplate(null);
                }}
                className="border-burgundy/30"
              >
                Back to Templates
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Campaign - Visual Flow Editor */}
      {automationView === 'edit-campaign' && selectedCampaign && (
        <CampaignFlowEditor
          campaign={selectedCampaign}
          templates={templates}
          onBack={() => {
            setAutomationView('campaigns');
            setSelectedCampaign(null);
            fetchCampaigns();
          }}
        />
      )}

      {/* Enrollment Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md border-burgundy/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-burgundy">Enroll Test User</h3>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowEnrollModal(false);
                    setEnrollEmail('');
                    setEnrollCampaignId(null);
                  }}
                  className="text-burgundy/60"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  User Email Address
                </label>
                <Input
                  type="email"
                  value={enrollEmail}
                  onChange={(e) => setEnrollEmail(e.target.value)}
                  placeholder="your-email@example.com"
                  className="border-burgundy/30"
                />
                <p className="text-xs text-burgundy/50 mt-2">
                  The user must already have an account. All campaign emails will be scheduled based on their delays.
                </p>
              </div>
              
              <div className="bg-cream/50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-burgundy mb-1">📧 What happens next:</p>
                <ul className="list-disc list-inside space-y-1 text-burgundy/70">
                  <li>User enrolled in this campaign</li>
                  <li>All emails scheduled with proper delays</li>
                  <li>Emails sent automatically every 5 minutes</li>
                  <li>First email (0 delay) sends within 5 minutes</li>
                </ul>
              </div>

              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEnrollModal(false);
                    setEnrollEmail('');
                    setEnrollCampaignId(null);
                  }}
                  className="border-burgundy/30"
                >
                  Cancel
                </Button>
                <Button
                  onClick={enrollUserInCampaign}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Users className="h-4 w-4 mr-2" />
                  Enroll User
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Send Test Email Modal */}
      {showTestEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md border-burgundy/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-burgundy">Send Test Email</h3>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmailAddress('');
                    setTestEmailTemplateId(null);
                  }}
                  className="text-burgundy/60"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">
                  Email Address
                </label>
                <Input
                  type="email"
                  value={testEmailAddress}
                  onChange={(e) => setTestEmailAddress(e.target.value)}
                  placeholder="your-email@example.com"
                  className="border-burgundy/30"
                />
                <p className="text-xs text-burgundy/50 mt-1">
                  We'll send a test version of this template to this address
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowTestEmailModal(false);
                    setTestEmailAddress('');
                    setTestEmailTemplateId(null);
                  }}
                  className="border-burgundy/30"
                >
                  Cancel
                </Button>
                <Button
                  onClick={sendTestEmail}
                  className="bg-burgundy hover:bg-burgundy/80"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Send Test
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

// Campaign Flow Editor Component
const CampaignFlowEditor = ({ campaign, templates, onBack }) => {
  const [emails, setEmails] = useState(campaign.emails || []);
  const [showAddEmail, setShowAddEmail] = useState(false);
  const [editingEmail, setEditingEmail] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(false);
  const [campaignDetails, setCampaignDetails] = useState({
    name: campaign.name,
    description: campaign.description
  });
  const [newEmail, setNewEmail] = useState({
    template_id: '',
    sequence_order: emails.length,
    delay_days: 0,
    delay_hours: 0
  });
  const [editEmail, setEditEmail] = useState({
    template_id: '',
    delay_days: 0,
    delay_hours: 0
  });

  const addEmailToCampaign = async () => {
    try {
      await axios.post(`${API}/admin/automation/campaigns/${campaign.id}/emails`, newEmail);
      toast.success('Email added to sequence');
      
      // Refresh campaign data
      const response = await axios.get(`${API}/admin/automation/campaigns/${campaign.id}`);
      setEmails(response.data.emails);
      
      setShowAddEmail(false);
      setNewEmail({
        template_id: '',
        sequence_order: emails.length + 1,
        delay_days: 0,
        delay_hours: 0
      });
    } catch (error) {
      toast.error('Failed to add email');
      console.error(error);
    }
  };

  const removeEmail = async (emailId) => {
    if (!window.confirm('Remove this email from the sequence?')) return;
    
    try {
      await axios.delete(`${API}/admin/automation/campaigns/${campaign.id}/emails/${emailId}`);
      toast.success('Email removed');
      setEmails(emails.filter(e => e.id !== emailId));
    } catch (error) {
      toast.error('Failed to remove email');
      console.error(error);
    }
  };

  const updateEmail = async () => {
    try {
      await axios.put(`${API}/admin/automation/campaigns/${campaign.id}/emails/${editingEmail.id}`, editEmail);
      toast.success('Email updated');
      
      // Refresh campaign data
      const response = await axios.get(`${API}/admin/automation/campaigns/${campaign.id}`);
      setEmails(response.data.emails);
      
      setEditingEmail(null);
      setEditEmail({
        template_id: '',
        delay_days: 0,
        delay_hours: 0
      });
    } catch (error) {
      toast.error('Failed to update email');
      console.error(error);
    }
  };

  const updateCampaignDetails = async () => {
    try {
      await axios.put(`${API}/admin/automation/campaigns/${campaign.id}`, campaignDetails);
      toast.success('Campaign updated successfully');
      setEditingCampaign(false);
      campaign.name = campaignDetails.name;
      campaign.description = campaignDetails.description;
    } catch (error) {
      toast.error('Failed to update campaign');
      console.error(error);
    }
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : 'Unknown Template';
  };

  return (
    <Card className="border-burgundy/20">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {editingCampaign ? (
              <div className="space-y-3">
                <Input
                  value={campaignDetails.name}
                  onChange={(e) => setCampaignDetails({...campaignDetails, name: e.target.value})}
                  placeholder="Campaign name"
                  className="text-lg font-semibold border-burgundy/30"
                />
                <Textarea
                  value={campaignDetails.description}
                  onChange={(e) => setCampaignDetails({...campaignDetails, description: e.target.value})}
                  placeholder="Campaign description"
                  className="text-sm border-burgundy/30"
                  rows="2"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={updateCampaignDetails}
                    className="bg-burgundy hover:bg-burgundy/80"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Changes
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setEditingCampaign(false);
                      setCampaignDetails({
                        name: campaign.name,
                        description: campaign.description
                      });
                    }}
                    className="border-burgundy/30"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold text-burgundy">{campaign.name}</h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingCampaign(true)}
                    className="text-burgundy/60 hover:bg-burgundy/10"
                    title="Edit campaign details"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-burgundy/60">{campaign.description || 'Visual Flow Editor'}</p>
              </div>
            )}
          </div>
          <Button variant="ghost" onClick={onBack} className="text-burgundy/60">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Timeline Visualization */}
        <div className="bg-cream/30 rounded-lg p-6">
          <h4 className="text-sm font-semibold text-burgundy mb-4">Email Sequence Timeline</h4>
          <div className="relative">
            {/* Timeline Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-burgundy/20" />
            
            {emails.sort((a, b) => a.sequence_order - b.sequence_order).map((email, index) => {
              const totalDelay = email.delay_days * 24 + (email.delay_hours || 0);
              const template = templates.find(t => t.id === email.template_id);
              
              return (
                <div key={email.id} className="relative flex items-start gap-4 mb-6 last:mb-0">
                  {/* Timeline Dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-burgundy flex items-center justify-center text-white font-bold">
                      Day {email.delay_days}
                    </div>
                    {email.delay_hours > 0 && (
                      <div className="text-xs text-center text-burgundy/60 mt-1">
                        +{email.delay_hours}h
                      </div>
                    )}
                  </div>
                  
                  {/* Email Card */}
                  <div className="flex-1">
                    <Card className="border-burgundy/20">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="font-semibold text-burgundy mb-1">
                              {template ? template.name : 'Template not found'}
                            </div>
                            {template && (
                              <>
                                <div className="text-sm text-burgundy/70 mb-1">
                                  <strong>Subject:</strong> {template.subject}
                                </div>
                                {template.preheader && (
                                  <div className="text-xs text-burgundy/50">
                                    {template.preheader}
                                  </div>
                                )}
                              </>
                            )}
                            <div className="text-xs text-burgundy/40 mt-2">
                              Sequence #{email.sequence_order + 1}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEditingEmail(email);
                                setEditEmail({
                                  template_id: email.template_id,
                                  delay_days: email.delay_days,
                                  delay_hours: email.delay_hours || 0
                                });
                              }}
                              className="text-burgundy hover:bg-burgundy/10"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removeEmail(email.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              );
            })}
            
            {/* Add Email Button */}
            <div className="relative flex items-center gap-4">
              <div className="w-16 h-16 rounded-full border-2 border-dashed border-burgundy/30 flex items-center justify-center text-burgundy/50">
                <Plus className="h-6 w-6" />
              </div>
              <Button
                onClick={() => setShowAddEmail(true)}
                variant="outline"
                className="border-burgundy/30 text-burgundy hover:bg-burgundy/10"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Email to Sequence
              </Button>
            </div>
          </div>
        </div>

        {/* Edit Email Form */}
        {editingEmail && (
          <Card className="border-burgundy/20 bg-blue-50 shadow-lg">
            <CardContent className="p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-burgundy">Edit Email in Sequence</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setEditingEmail(null);
                    setEditEmail({
                      template_id: '',
                      delay_days: 0,
                      delay_hours: 0
                    });
                  }}
                  className="text-burgundy/60"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Select Template</label>
                <select
                  value={editEmail.template_id}
                  onChange={(e) => setEditEmail({...editEmail, template_id: e.target.value})}
                  className="w-full p-2 border border-burgundy/20 rounded-md"
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Delay (Days)</label>
                  <Input
                    type="number"
                    min="0"
                    value={editEmail.delay_days}
                    onChange={(e) => setEditEmail({...editEmail, delay_days: parseInt(e.target.value) || 0})}
                    className="border-burgundy/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Additional Hours</label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={editEmail.delay_hours}
                    onChange={(e) => setEditEmail({...editEmail, delay_hours: parseInt(e.target.value) || 0})}
                    className="border-burgundy/20"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={updateEmail}
                  className="bg-burgundy hover:bg-burgundy/80"
                >
                  Save Changes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditingEmail(null);
                    setEditEmail({
                      template_id: '',
                      delay_days: 0,
                      delay_hours: 0
                    });
                  }}
                  className="border-burgundy/30"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Add Email Form */}
        {showAddEmail && (
          <Card className="border-burgundy/20 bg-gold/5">
            <CardContent className="p-4 space-y-4">
              <h4 className="font-semibold text-burgundy">Add Email to Sequence</h4>
              <div>
                <label className="block text-sm font-medium text-burgundy mb-1">Select Template</label>
                <select
                  value={newEmail.template_id}
                  onChange={(e) => setNewEmail({...newEmail, template_id: e.target.value})}
                  className="w-full p-2 border border-burgundy/20 rounded-md"
                >
                  <option value="">-- Select a template --</option>
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>{template.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Delay (Days)</label>
                  <Input
                    type="number"
                    min="0"
                    value={newEmail.delay_days}
                    onChange={(e) => setNewEmail({...newEmail, delay_days: parseInt(e.target.value) || 0})}
                    className="border-burgundy/20"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-burgundy mb-1">Additional Hours</label>
                  <Input
                    type="number"
                    min="0"
                    max="23"
                    value={newEmail.delay_hours}
                    onChange={(e) => setNewEmail({...newEmail, delay_hours: parseInt(e.target.value) || 0})}
                    className="border-burgundy/20"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={addEmailToCampaign}
                  disabled={!newEmail.template_id}
                  className="bg-burgundy hover:bg-burgundy/80"
                >
                  Add Email
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAddEmail(false);
                    setNewEmail({
                      template_id: '',
                      sequence_order: emails.length,
                      delay_days: 0,
                      delay_hours: 0
                    });
                  }}
                  className="border-burgundy/30"
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
};

export default CRMTab;
